// Pure tree-building logic for the in-thread debug event timeline: buckets a
// flat thread-event list into turns, nests sub-turns, and clusters adjacent
// turns/debug events for the collapsible UI. Single source consumed by both
// web (components/messages/EventItems.tsx) and native (features/threads/debug).
// Dependency-free (types only) so it lives here in shared/client and stays
// unit-testable.
import type { DebugThreadEvent, MessageThreadEvent, ThreadEventItem } from "@/types/threadEvents.ts";

export type NonMessageThreadEvent = Exclude<ThreadEventItem, MessageThreadEvent>;
export type PrimaryThreadEvent = Exclude<NonMessageThreadEvent, DebugThreadEvent>;

export interface TurnNode {
  turnId: string;
  parentTurnId: string | null;
  events: NonMessageThreadEvent[];
  childTurns: TurnNode[];
  startedAt: string;
  firstEventId: number;
}

/** A single member of a child cluster — either a sub-turn or a debug event. */
export type ChildClusterItem =
  | { kind: "subturn"; node: TurnNode }
  | { kind: "debug"; event: DebugThreadEvent };

export const childClusterItemKey = (item: ChildClusterItem) =>
  item.kind === "subturn" ? `turn-${item.node.turnId}` : `debug-${item.event.id}`;

export type MessageRootItem = { kind: "message"; event: MessageThreadEvent };
export type TurnRootItem = { kind: "turn"; node: TurnNode };
export type ChildClusterRootItem = { kind: "cluster"; items: ChildClusterItem[] };
export type OrphanRootItem = { kind: "orphan"; event: NonMessageThreadEvent };

export type RootItem = MessageRootItem | TurnRootItem | ChildClusterRootItem | OrphanRootItem;

const timelineKey = (item: MessageRootItem | TurnRootItem | OrphanRootItem) => {
  if (item.kind === "turn") {
    return { sortDate: item.node.startedAt, id: item.node.firstEventId };
  }
  return { sortDate: item.event.sortDate, id: item.event.id };
};

const compareTimelineItems = (
  a: { sortDate: string; id: number },
  b: { sortDate: string; id: number },
) => {
  const dateDiff = new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime();
  return dateDiff !== 0 ? dateDiff : a.id - b.id;
};

const sortEvents = (events: NonMessageThreadEvent[]) =>
  [...events].sort((a, b) =>
    compareTimelineItems({ sortDate: a.sortDate, id: a.id }, { sortDate: b.sortDate, id: b.id }),
  );

const sortTurns = (turns: TurnNode[]) =>
  [...turns].sort((a, b) =>
    compareTimelineItems(
      { sortDate: a.startedAt, id: a.firstEventId },
      { sortDate: b.startedAt, id: b.firstEventId },
    ),
  );

/**
 * True if attaching `node` beneath `parent` would form a cycle — i.e. `node`
 * already sits on `parent`'s ancestry chain (including `parent` itself, for a
 * self-parent link). parentTurnId comes from the server, which emits a tree by
 * construction, so this only trips on malformed data; when it does, the caller
 * roots the offending node instead, keeping the tree acyclic so the recursive
 * timeline renderer can't stack-overflow.
 */
function wouldFormCycle(
  node: TurnNode,
  parent: TurnNode,
  turnNodes: Map<string, TurnNode>,
): boolean {
  const seen = new Set<string>();
  let cursor: TurnNode | undefined = parent;
  while (cursor) {
    if (cursor.turnId === node.turnId) return true;
    if (seen.has(cursor.turnId)) return true; // pre-existing cycle among ancestors
    seen.add(cursor.turnId);
    cursor = cursor.parentTurnId ? turnNodes.get(cursor.parentTurnId) : undefined;
  }
  return false;
}

export function buildRootItems(items: ThreadEventItem[], isEventView: boolean): RootItem[] {
  if (!isEventView) {
    // Messages view: the feed only contains MessageThreadEvents by invariant.
    return items
      .filter((item): item is MessageThreadEvent => item.type === "message")
      .map((event) => ({ kind: "message" as const, event }));
  }

  const messageItems: MessageRootItem[] = [];
  const orphanEvents: NonMessageThreadEvent[] = [];
  const turnBuckets = new Map<string, NonMessageThreadEvent[]>();

  for (const item of items) {
    if (item.type === "message") {
      messageItems.push({ kind: "message", event: item });
      continue;
    }
    if (!item.turnId) {
      orphanEvents.push(item);
      continue;
    }
    const bucket = turnBuckets.get(item.turnId);
    if (bucket) {
      bucket.push(item);
    } else {
      turnBuckets.set(item.turnId, [item]);
    }
  }

  const turnNodes = new Map<string, TurnNode>();
  for (const [turnId, bucket] of turnBuckets) {
    const events = sortEvents(bucket);
    const firstEvent = events[0];
    if (!firstEvent) continue;
    turnNodes.set(turnId, {
      turnId,
      parentTurnId: firstEvent.parentTurnId,
      events,
      childTurns: [],
      startedAt: firstEvent.sortDate,
      firstEventId: firstEvent.id,
    });
  }

  const rootTurns: TurnNode[] = [];
  for (const node of turnNodes.values()) {
    const parent = node.parentTurnId ? turnNodes.get(node.parentTurnId) : undefined;
    if (parent && !wouldFormCycle(node, parent, turnNodes)) {
      parent.childTurns.push(node);
    } else {
      rootTurns.push(node);
    }
  }

  for (const node of turnNodes.values()) {
    node.childTurns = sortTurns(node.childTurns);
  }

  const preClusterItems: (MessageRootItem | TurnRootItem | OrphanRootItem)[] = [
    ...messageItems,
    ...sortTurns(rootTurns).map((node) => ({ kind: "turn" as const, node })),
    ...sortEvents(orphanEvents).map((event) => ({ kind: "orphan" as const, event })),
  ];

  const sorted = [...preClusterItems].sort((a, b) =>
    compareTimelineItems(timelineKey(a), timelineKey(b)),
  );

  // Cluster runs of adjacent turn items and orphan debug events into a single
  // mixed "N sub actions, M debug" collapsible. Messages and non-debug
  // orphans break the run.
  const clustered: RootItem[] = [];
  let pending: ChildClusterItem[] = [];
  const flush = () => {
    if (pending.length === 0) return;
    clustered.push({ kind: "cluster", items: pending });
    pending = [];
  };
  for (const item of sorted) {
    if (item.kind === "turn") {
      pending.push({ kind: "subturn", node: item.node });
    } else if (item.kind === "orphan" && item.event.type === "debug") {
      pending.push({ kind: "debug", event: item.event });
    } else {
      flush();
      clustered.push(item);
    }
  }
  flush();
  return clustered;
}

// ---------------------------------------------------------------------------
// Turn segments
// ---------------------------------------------------------------------------

export type TurnSegment =
  | { kind: "primary"; event: PrimaryThreadEvent }
  | { kind: "cluster"; items: ChildClusterItem[] };

export function buildTurnSegments(node: TurnNode): TurnSegment[] {
  const segments: TurnSegment[] = [];
  let pending: ChildClusterItem[] = [];
  const events = sortEvents(node.events);
  const childTurns = sortTurns(node.childTurns);
  let eventIndex = 0;
  let childIndex = 0;

  const flush = () => {
    if (pending.length > 0) {
      segments.push({ kind: "cluster", items: pending });
      pending = [];
    }
  };

  while (eventIndex < events.length || childIndex < childTurns.length) {
    const nextEvent = events[eventIndex];
    const nextChild = childTurns[childIndex];

    const shouldTakeEvent =
      nextEvent &&
      (!nextChild ||
        compareTimelineItems(
          { sortDate: nextEvent.sortDate, id: nextEvent.id },
          { sortDate: nextChild.startedAt, id: nextChild.firstEventId },
        ) <= 0);

    if (shouldTakeEvent && nextEvent) {
      eventIndex += 1;
      if (nextEvent.type === "debug") {
        pending.push({ kind: "debug", event: nextEvent });
      } else {
        flush();
        segments.push({ kind: "primary", event: nextEvent });
      }
      continue;
    }

    if (nextChild) {
      childIndex += 1;
      pending.push({ kind: "subturn", node: nextChild });
    }
  }

  flush();
  return segments;
}
